import React, { useState } from 'react'
import { Dropdown, Button, Tooltip, Space, Typography } from 'antd'
import { useSelector } from 'react-redux'
import { DeleteOutlined, EditOutlined, MoreOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { CommentForm } from './CommentForm'
import type { BudgetComment } from '../../types'
import { currentUserSelector } from '../../../../redux/session/currentUser/selectors'
import { useDateFormatter } from '../../../../shared/hooks/date/useDateFormatter'
import { useCtxCompanyUsers } from '../../../users/queries/useCtxCompanyUsers'
import { useDeleteConfirmModal } from '../../../../shared/modals/deleteConfirmModal'

interface CommentItemProps {
  comment: BudgetComment
  onUpdate: (text: string) => void | Promise<void>
  onDelete: () => void | Promise<void>
}

const { Text } = Typography

const CommentContainer = styled.div`
  padding: 12px 0;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
  border-bottom: 2px solid #e8e8e8;

  &:last-child {
    border-bottom: none;
  }
`

const CommentContent = styled.div`
  flex: 1;
  min-width: 0;
`

const CommentHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`

const CommentText = styled(Text)`
  word-break: break-word;
  white-space: pre-wrap;
  display: block;
  font-family: 'Noto Sans', Tahoma, Geneva, Verdana, sans-serif;
  font-size: 12px;
  line-height: 1.4;
  letter-spacing: 0.01em;
  color: rgba(0, 0, 0, 0.85);
`

/**
 * Single comment item with edit/delete actions
 */
export const CommentItem: React.FC<CommentItemProps> = ({ comment, onUpdate, onDelete }) => {
  const { t } = useTranslation()
  const currentUser = useSelector(currentUserSelector)
  const { data: companyUsers = [] } = useCtxCompanyUsers()
  const [isEditing, setIsEditing] = useState(false)
  const dateFormatter = useDateFormatter()
  const deleteConfirmModal = useDeleteConfirmModal()
  // Find comment author
  const author = [currentUser, ...companyUsers].find(user => user?.id === comment.userId)
  const isOwnComment = currentUser?.id === comment.userId

  // Check if comment was edited
  const wasEdited = comment.updatedAt && comment.updatedAt !== comment.createdAt

  const handleUpdate = async (text: string) => {
    await onUpdate(text)
    setIsEditing(false)
  }

  const handleDelete = () => {
    deleteConfirmModal({
      content: comment.text.length > 50 ? `${comment.text.substring(0, 50)}...` : comment.text,
      onOk: onDelete
    })
  }

  const menuItems = [
    {
      key: 'edit',
      icon: <EditOutlined />,
      label: t('global:edit', 'Edit'),
      onClick: () => setIsEditing(true)
    },
    {
      key: 'delete',
      icon: <DeleteOutlined />,
      label: t('global:delete', 'Delete'),
      onClick: handleDelete,
      danger: true
    }
  ]

  return (
    <CommentContainer>
      <CommentContent>
        <Space direction="vertical" size={4} style={{ width: '100%' }}>
          {/* Author and timestamp */}
          <CommentHeader>
            <Text strong>{author?.displayName || 'Unknown user'}</Text>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {dateFormatter(comment.createdAt, 'standardWithTime')}
              {wasEdited && (
                <>
                  {' • '}
                  <Tooltip
                    title={`${t('global:edited', 'muokattu')}: ${dateFormatter(comment.updatedAt, 'standardWithTime')}`}
                  >
                    <span style={{ cursor: 'help', textDecoration: 'underline dotted' }}>
                      {t('global:edited', 'muokattu')}
                    </span>
                  </Tooltip>
                </>
              )}
            </Text>
          </CommentHeader>

          {/* Comment text or edit form */}
          {isEditing ? (
            <CommentForm
              initialValue={comment.text}
              onSubmit={handleUpdate}
              onCancel={() => setIsEditing(false)}
              submitText={t('global:update', 'Update')}
            />
          ) : (
            <CommentText>{comment.text}</CommentText>
          )}
        </Space>
      </CommentContent>

      {/* Actions menu (only for own comments) */}
      {isOwnComment && (
        <Dropdown trigger={['click']} menu={{ items: menuItems }} placement="bottomRight">
          <Button size="small" icon={<MoreOutlined />} />
        </Dropdown>
      )}
    </CommentContainer>
  )
}
